/* eslint-disable react/no-array-index-key */
import { ReactElement, useCallback, useMemo, useRef } from "react";

import { ObservableQuery } from "@apollo/client";
import { Box, Flex, Text, IconButton, Image } from "@chakra-ui/react";
import { datadogRum } from "@datadog/browser-rum";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import {
  InboundingListProduct,
  InboundingListStatus,
  ListProductStatus,
  CommonInboundingType,
  InboundingList,
} from "__graphql__/types";
import { CollaborativeInboundUpdateState } from "analytics/events";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { useFetchInboundPreDroppingProducts } from "flows/Inbound/stores/useInboundProductSearchService";
import useSwipeScroll from "shared/hooks/useSwipeScroll";
import { IntlMessageId } from "shared/lang";
import {
  ThreedotsIcon,
  NewProfileIcon,
  FilledPencilIcon,
  TrashIcon,
  EmptyListIcon,
  PenIcon,
  FlinkBagIcon,
  GroupUserIcon,
} from "ui/Icons/Icons";
import { Menu } from "ui/Menu/Menu";
import { SideButton } from "ui/SideButton/SideButton";
import { SwipeScrollWrapper } from "ui/SwipeScrollWrapper";
import { DetailL, TitleXS } from "ui/Typography/Typography";
import { getFormattedDate } from "utils/formats";
import { formatImage } from "utils/image";

import { useInboundEventTracker } from "../hooks/useInboundEventTracker/useInboundEventTracker";
import { useSharedListClaimerName } from "../hooks/useSharedListClaimerName";
import { useGetMultipleDesadvsRolliIdLazyQuery } from "../queries/despatchAdvice/despatchAdvice.generated";
import { useInboundStore } from "../stores/inboundStore/useInboundStore";
import { mapInboundingTypeToDeliveryType } from "../stores/inboundStore/utils";
import { useClaimInboundList } from "../stores/useInboundDropService";
import { useInboundPreviewService } from "../stores/useInboundPreviewService";

type SharedListComponentProps = {
  creatorId: string | null;
  orderId: string | null;
  listId: string;
  listName: string;
  deliverySSCC: string;
  inboundingType: CommonInboundingType | null;
  createdAt: string;
  status: InboundingListStatus;
  products?: InboundingListProduct[] | null;
  claimerIds?: InboundingList["claimerIds"];
  isOrderCanceled?: boolean;
  onOpenDeleteListConfirmationModal(): void;
  refetchInboundingLists?: ObservableQuery["refetch"];
};

const getFormattedProductImages = (products: InboundingListProduct[] | null) => {
  return products
    ?.map((product?: InboundingListProduct) => product?.imageUrl || null)
    .filter((url: string | null): url is string => url !== null)
    .slice(0, 5);
};

function MenuItemWrapper({
  icon,
  labelId,
  onClick,
}: {
  icon: ReactElement;
  labelId: IntlMessageId;
  onClick: () => void;
}) {
  return (
    <Menu.Item icon={icon} sx={{ _hover: { bg: "transparent" } }} onClick={onClick} paddingY="s150">
      <FormattedMessage id={labelId} />
    </Menu.Item>
  );
}

function HeaderSection({
  listName,
  status,
  isOrderCanceled,
  onDeleteSharedList,
  onClaimSharedList,
  onEditSharedListName,
}: {
  listName: string;
  status: InboundingListStatus;
  isOrderCanceled: boolean;
  onDeleteSharedList(): void;
  onClaimSharedList(): void;
  onEditSharedListName(): void;
}) {
  const isDraft = status === InboundingListStatus.inbounding_list_status_draft;
  const isUnclaimed = status === InboundingListStatus.inbounding_list_status_unclaimed;
  const isClaimed = status === InboundingListStatus.inbounding_list_status_claimed;
  const showMenu = useMemo(() => {
    return isUnclaimed || isDraft || isClaimed;
  }, [isUnclaimed, isDraft, isClaimed]);
  const showClaimOption = useMemo(() => {
    return isUnclaimed || isClaimed;
  }, [isUnclaimed, isClaimed]);

  const Icon = useMemo(() => {
    if (isOrderCanceled) return FlinkBagIcon;
    if (isDraft) return PenIcon;
    return EmptyListIcon;
  }, [isOrderCanceled, isDraft]);

  const MenuContent = useMemo(() => {
    if (!isOrderCanceled && !showMenu) return null;

    return (
      <Menu>
        <Menu.Trigger
          as={IconButton}
          icon={<ThreedotsIcon boxSize="24px" />}
          variant="unstyled"
          aria-label="Options"
        />
        <Menu.List minWidth="140px" borderRadius="sm" width="auto" mt="-45px" boxShadow="lg">
          {isOrderCanceled ? (
            <>
              <MenuItemWrapper
                icon={<NewProfileIcon boxSize={6} />}
                labelId="flows.inbound.components.shared-list-component.menu-claim"
                onClick={onClaimSharedList}
              />
              <MenuItemWrapper
                icon={<TrashIcon boxSize={6} />}
                labelId="flows.inbound.components.shared-list-component.menu-order-not-returned"
                onClick={onDeleteSharedList}
              />
            </>
          ) : (
            <>
              {showClaimOption && (
                <MenuItemWrapper
                  icon={<NewProfileIcon boxSize={6} />}
                  labelId="flows.inbound.components.shared-list-component.menu-claim"
                  onClick={onClaimSharedList}
                />
              )}
              <MenuItemWrapper
                icon={<FilledPencilIcon boxSize={6} />}
                labelId="flows.inbound.components.shared-list-component.menu-edit"
                onClick={onEditSharedListName}
              />
              <MenuItemWrapper
                icon={<TrashIcon boxSize={6} />}
                labelId="flows.inbound.components.shared-list-component.menu-delete"
                onClick={onDeleteSharedList}
              />
            </>
          )}
        </Menu.List>
      </Menu>
    );
  }, [
    isOrderCanceled,
    showMenu,
    showClaimOption,
    onClaimSharedList,
    onEditSharedListName,
    onDeleteSharedList,
  ]);

  return (
    <Flex
      justifyContent="space-between"
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="grey.200"
      alignItems="center"
      pb="s100"
    >
      <Flex alignItems="center">
        <Icon boxSize="24px" />
        <TitleXS ml={2}>{listName}</TitleXS>
      </Flex>
      {MenuContent}
    </Flex>
  );
}

function ProductSection({ products }: { products?: InboundingListProduct[] | null }) {
  if (!products) return null;

  const remainingProductsCount = products.length > 5 ? products.length - 5 : 0;
  const productImages = getFormattedProductImages(products);

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex align="center" gap={1.5}>
        {productImages &&
          productImages.map((imageUrl, index) => (
            <Box key={index} borderRadius="sm" overflow="hidden" boxShadow="sm">
              <Image
                src={imageUrl}
                srcSet={`${formatImage(imageUrl, 38, 38)} 1x, ${formatImage(imageUrl, 76, 76)} 2x`}
                alt={imageUrl}
                boxSize="38px"
              />
            </Box>
          ))}
      </Flex>
      {remainingProductsCount > 0 && (
        <Box
          borderRadius="sm"
          overflow="hidden"
          boxShadow="sm"
          display="flex"
          alignItems="center"
          justifyContent="center"
          backgroundColor="grey.100"
          boxSize="38px"
        >
          <Text color="grey.500">+{remainingProductsCount}</Text>
        </Box>
      )}
    </Flex>
  );
}

function FooterSection({
  totalProducts,
  totalNonInboundedProducts,
  createdAt,
  isOrderCanceled,
}: {
  totalProducts: number;
  totalNonInboundedProducts: number;
  createdAt: string;
  isOrderCanceled: boolean;
}) {
  const intl = useIntl();
  const date = isOrderCanceled
    ? dayjs(createdAt).format("DD/MM - HH:mm")
    : getFormattedDate(createdAt, intl);

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Box w="40%">
        <DetailL color="grey.500">
          <FormattedMessage id="flows.inbound.components.shared-list-component.date-created" />
        </DetailL>
        <TitleXS>{date}</TitleXS>
      </Box>

      <Box w="60%">
        <DetailL color="grey.500">
          <FormattedMessage id="flows.inbound.components.shared-list-component.products-not-inbounded" />
        </DetailL>
        <TitleXS>
          {intl.formatMessage(
            { id: "flows.inbound.components.shared-list-component.total-products" },
            { totalNonInboundedProducts, totalProducts },
          )}
        </TitleXS>
      </Box>
    </Flex>
  );
}

const LEAST_NUMBER_OF_CLAIMERS = 1;

export function SharedListComponent({
  creatorId,
  orderId,
  listId,
  listName,
  deliverySSCC,
  inboundingType,
  createdAt,
  status,
  products,
  claimerIds,
  isOrderCanceled = false,
  onOpenDeleteListConfirmationModal,
  refetchInboundingLists,
}: SharedListComponentProps) {
  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRefFromStore = useLayoutStore((state) => state.scrollContainerRef);
  const { handleSwipeScroll, isScrolledLeft } = useSwipeScroll(
    scrollContainerRefFromStore,
    wrapperRef,
  );
  const deliveryType = mapInboundingTypeToDeliveryType(inboundingType);
  const { claimInboundList } = useClaimInboundList(refetchInboundingLists);
  const {
    setDeliverySSCC,
    setSelectedOption,
    setCreatorId,
    setOrderId,
    setSharedListId,
    setSharedListName,
    updateDespatchAdviceItems,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      setDeliverySSCC: state.setDeliverySSCC,
      setSelectedOption: state.setSelectedOption,
      setCreatorId: state.setCreatorId,
      setOrderId: state.setOrderId,
      setSharedListId: state.setSharedListId,
      setSharedListName: state.setSharedListName,
      updateDespatchAdviceItems: state.updateDespatchAdviceItems,
      setInboundUIState: state.setInboundUIState,
    })),
  );
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const { fetchInboundPreviewProducts } = useInboundPreviewService();
  const { fetchInboundPreDroppingProducts } = useFetchInboundPreDroppingProducts();

  const [fetchDesadvs] = useGetMultipleDesadvsRolliIdLazyQuery({
    onCompleted: (data) =>
      updateDespatchAdviceItems(data?.getMultipleDesadvsRolliID?.despatchAdvices ?? null),
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      datadogRum.addError("Error fetching despatch advices for delivery SSCC:", { errorMessage });
    },
  });

  const { badgeNo } = useEmployeeStore(
    useShallow((state) => ({
      badgeNo: state.badgeNo,
    })),
  );

  const handleListClick = useCallback(async () => {
    setCreatorId(creatorId);
    setOrderId(orderId);
    setSharedListId(listId);
    setSharedListName(listName);
    setSelectedOption(deliveryType);
    setInboundUIState({ isOrderCanceled });
    if (deliverySSCC) {
      setDeliverySSCC(deliverySSCC);
      await fetchDesadvs({ variables: { input: { rolliID: deliverySSCC } } });
    }
    if (status === InboundingListStatus.inbounding_list_status_draft) {
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.ListPreparationResumed,
        droppingListId: listId,
        sscc: deliverySSCC,
        inboundingType: deliveryType,
        creatorId,
      });
      await fetchInboundPreDroppingProducts(products);
    } else {
      await fetchInboundPreviewProducts(products);
    }
  }, [
    creatorId,
    deliverySSCC,
    deliveryType,
    fetchDesadvs,
    fetchInboundPreDroppingProducts,
    fetchInboundPreviewProducts,
    isOrderCanceled,
    listId,
    listName,
    orderId,
    products,
    sendInboundStateUpdate,
    setCreatorId,
    setDeliverySSCC,
    setInboundUIState,
    setOrderId,
    setSelectedOption,
    setSharedListId,
    setSharedListName,
    status,
  ]);

  const onClickClaimSharedList = useCallback(() => {
    setCreatorId(creatorId);
    setOrderId(orderId);
    setDeliverySSCC(deliverySSCC);
    setSelectedOption(deliveryType);
    claimInboundList({ listId, deliverySSCC, deliveryType, creatorId, orderId, isOrderCanceled });
  }, [
    claimInboundList,
    creatorId,
    deliverySSCC,
    deliveryType,
    isOrderCanceled,
    listId,
    orderId,
    setCreatorId,
    setDeliverySSCC,
    setOrderId,
    setSelectedOption,
  ]);

  const onEditSharedListName = useCallback(() => {
    setInboundUIState({ isSharedListNameEditActive: true });
    handleListClick();
  }, [handleListClick, setInboundUIState]);

  const notInboundedProducts = (products || []).filter(
    (product) =>
      (product.status && product.status !== ListProductStatus.list_product_status_inbounded) ||
      product.isInbounded === false,
  );

  const sharedListclaimerIds = claimerIds || [];
  const shouldShowEmployeeName = sharedListclaimerIds.length === LEAST_NUMBER_OF_CLAIMERS;
  const employeeName = useSharedListClaimerName({
    badgeNo,
    claimerIds: sharedListclaimerIds,
    skip: !shouldShowEmployeeName,
  });

  const mainComponent = (
    <Flex
      direction="column"
      bg="white"
      borderRadius={isScrolledLeft ? "none" : "md"}
      p={4}
      gap={4}
      cursor="pointer"
      id="shared-list-component"
    >
      <HeaderSection
        listName={listName}
        onDeleteSharedList={onOpenDeleteListConfirmationModal}
        onClaimSharedList={onClickClaimSharedList}
        onEditSharedListName={onEditSharedListName}
        status={status}
        isOrderCanceled={isOrderCanceled}
      />
      <Flex direction="column" gap={2}>
        <Flex direction="column" gap={4} onClick={handleListClick}>
          <ProductSection products={notInboundedProducts} />
          <FooterSection
            totalProducts={products?.length ?? 0}
            totalNonInboundedProducts={notInboundedProducts?.length ?? 0}
            createdAt={createdAt}
            isOrderCanceled={isOrderCanceled}
          />
        </Flex>
        {sharedListclaimerIds.length >= LEAST_NUMBER_OF_CLAIMERS && (
          <Flex alignItems="center" gap={2}>
            <GroupUserIcon fill="none" />
            <DetailL>
              {sharedListclaimerIds.length > LEAST_NUMBER_OF_CLAIMERS
                ? intl.formatMessage(
                    { id: "flows.inbound.components.shared-list-component.people-on-list" },
                    { claimers: sharedListclaimerIds.length },
                  )
                : intl.formatMessage(
                    { id: "flows.inbound.components.shared-list-component.working-on-list" },
                    {
                      name: employeeName,
                    },
                  )}
            </DetailL>
          </Flex>
        )}
      </Flex>
    </Flex>
  );

  const claimListAction = (
    <Box flex="0 0 137px">
      <SideButton
        color="marine.500"
        bgColor="marine.200"
        icon={<NewProfileIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "flows.inbound.components.shared-list-component.button",
          defaultMessage: "Claim list",
        })}
        onClick={onClickClaimSharedList}
        data-testid="remove-product"
        height="100%"
        borderRightRadius="md"
      />
    </Box>
  );

  return (
    <SwipeScrollWrapper
      mainComponent={mainComponent}
      handleSwipeScroll={handleSwipeScroll}
      actionsRight={claimListAction}
      showActionsRight={status === InboundingListStatus.inbounding_list_status_unclaimed}
      ref={wrapperRef}
    />
  );
}
